import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
} from 'recharts'
import { CHART_COLORS } from '../../../constants/colors'
import type { TopicComparisonRadarData } from '../../../types'

interface TopicComparisonChartProps {
  data: TopicComparisonRadarData[]
}

const BAR_COLORS = [
  CHART_COLORS.primary,
  CHART_COLORS.secondary,
  CHART_COLORS.tertiary,
  CHART_COLORS.quaternary,
  CHART_COLORS.danger,
  '#EC4899', // pink
  '#06B6D4', // cyan
  '#84CC16', // lime
  '#F97316', // orange
  '#6366F1', // indigo
]

export default function TopicComparisonChart({ data }: TopicComparisonChartProps) {
  if (!data || data.length < 2) {
    return (
      <div className="h-64 flex items-center justify-center text-gray-500">
        Select at least 2 topics to compare
      </div>
    )
  }

  // Transform data for grouped horizontal bar chart
  // For Avg/Exam, we scale the values by 10 for better visibility but store actual values for labels
  const chartData = [
    {
      metric: 'Questions',
      ...data.reduce((acc, item, idx) => {
        acc[`topic${idx}`] = item.totalQuestions
        acc[`topic${idx}_actual`] = item.totalQuestions
        return acc
      }, {} as Record<string, number>),
    },
    {
      metric: 'Avg/Exam',
      ...data.reduce((acc, item, idx) => {
        acc[`topic${idx}`] = item.avgQuestionsPerExam * 10 // Scale for visibility
        acc[`topic${idx}_actual`] = item.avgQuestionsPerExam // Store actual value
        return acc
      }, {} as Record<string, number>),
    },
    {
      metric: 'Importance',
      ...data.reduce((acc, item, idx) => {
        acc[`topic${idx}`] = item.importanceScore
        acc[`topic${idx}_actual`] = item.importanceScore
        return acc
      }, {} as Record<string, number>),
    },
    {
      metric: 'Frequency %',
      ...data.reduce((acc, item, idx) => {
        acc[`topic${idx}`] = item.appearanceFrequency
        acc[`topic${idx}_actual`] = item.appearanceFrequency
        return acc
      }, {} as Record<string, number>),
    },
  ]

  // Truncate topic names for legend
  const truncateName = (name: string, maxLength: number = 18) => {
    if (name.length <= maxLength) return name
    return name.substring(0, maxLength) + '...'
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={chartData}
        layout="vertical"
        margin={{ top: 10, right: 20, left: 75, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke={CHART_COLORS.grid} horizontal={true} vertical={false} />
        <XAxis
          type="number"
          tick={{ fontSize: 11 }}
          tickLine={false}
          axisLine={{ stroke: CHART_COLORS.grid }}
        />
        <YAxis
          type="category"
          dataKey="metric"
          tick={{ fontSize: 12 }}
          tickLine={false}
          axisLine={{ stroke: CHART_COLORS.grid }}
          width={70}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: 'white',
            border: '1px solid #E5E7EB',
            borderRadius: '8px',
            padding: '8px 12px',
            fontSize: '12px',
          }}
          formatter={(value, name, props) => {
            const topicIndex = parseInt(String(name).replace('topic', ''))
            const topicName = data[topicIndex]?.topic || 'Unknown'
            // Show actual value from payload
            const actualValue = (props.payload as Record<string, number>)?.[`topic${topicIndex}_actual`] ?? value
            return [Number(actualValue).toFixed(1), truncateName(topicName, 30)]
          }}
        />
        <Legend
          formatter={(value: string) => {
            const topicIndex = parseInt(value.replace('topic', ''))
            return truncateName(data[topicIndex]?.topic || 'Unknown', 18)
          }}
          wrapperStyle={{ fontSize: '11px', paddingTop: '10px' }}
        />
        {data.map((_, index) => (
          <Bar
            key={`topic${index}`}
            dataKey={`topic${index}`}
            fill={BAR_COLORS[index % BAR_COLORS.length]}
            radius={[0, 4, 4, 0]}
            barSize={data.length > 4 ? 12 : 16}
          >
            <LabelList
              dataKey={`topic${index}_actual`}
              position="insideRight"
              fill="#fff"
              fontSize={10}
              fontWeight={500}
              formatter={(value) => Number(value)?.toFixed(1)}
            />
          </Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>
  )
}
